import React from 'react';
import styled, { css } from 'styled-components';
import Divider from '~/components/atom/Divider';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { ExternalLink } from '~/components/molecule/Link';
import { Heading5, Variant } from '~/components/atom/Typography';
import getPriceWithInterval, {
  PriceWithInterval,
} from '~/util/getPriceWithInterval';

const text = {
  price: 'Kosten',
  compatibility: 'Compatibiliteit',
  info: 'Informatie',
  provider: 'Aanbieder',
  learnMore: 'Meer over deze app',
};

export type Props = {
  /** Price of the app */
  price: PriceWithInterval | null | Array<PriceWithInterval | null>;

  /** Who can use the app */
  compatibility: string;

  provider: string;

  /** Link for help article */
  help?: {
    href: string;
  };
};

const Aside: React.FCC<Props> = ({
  dataTestId,
  children,
  price,
  compatibility,
  provider,
  help,
}) => {
  const priceStr = Array.isArray(price)
    ? `vanaf ${getPriceWithInterval(price[0])}`
    : getPriceWithInterval(price);

  return (
    <Container data-testid={dataTestId}>
      <JustificationContainer
        margin={[null, null, 'l', null]}
        direction="column"
      >
        {children}
      </JustificationContainer>

      <Heading5 variant={Variant.primary}>{text.price}</Heading5>
      <PriceContainer>{priceStr}</PriceContainer>

      <Heading5 variant={Variant.primary}>{text.compatibility}</Heading5>
      <span>{compatibility}</span>
      <Divider color={{ group: 'tertiary', variant: 'base' }} />

      <Heading5 variant={Variant.primary}>{text.provider}</Heading5>
      <span>{provider}</span>

      {help && (
        <>
          <Divider color={{ group: 'tertiary', variant: 'base' }} />
          <Heading5 variant={Variant.primary}>{text.info}</Heading5>
          <ExternalLink
            to={help.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {text.learnMore}
          </ExternalLink>
        </>
      )}
    </Container>
  );
};

const Container = styled.aside<{}>(
  ({ theme }) => css`
    background-color: ${theme.color('tertiary', 'light')};
    padding: ${theme.space('l')};
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    padding-bottom: ${theme.space('xxxl')};
  `,
);

const PriceContainer = styled.span<{}>(
  ({ theme }) => css`
    font-weight: ${theme.fontWeight('semiBold')};
    font-size: ${theme.fontSize('m')};
    margin-bottom: ${theme.space('l')};
  `,
);

export default Aside;
